import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DailyReportService } from '../../tab-screens/daily-report/daily-report.service';
import { GetVideoIdRequest, UploadFileRequest } from '../../../../../../shared/core/shared.actions';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../state/app.state';
import { Subject } from 'rxjs';
import { LocationPickerLauncherService } from '../../../../../../shared/services/location-picker-launcher.service';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { currentLocation, dailyReport, projectDetails, siteDetails } from '../../../../core/projects.selectors';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectsService } from '../../../../core/projects.service';
import { takeUntil } from 'rxjs/operators';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../shared/components';
import { CommonService } from '../../../../../../shared/services/common.service';
import { appDataDetails, dailyReportLocationConfig, limits } from '../../../../../../shared/core/shared.selectors';
import { Ability, subject } from '@casl/ability';
import {
  AddCloseCommentComponent,
} from '../../tab-screens/daily-report/daily-report-components/detail-components/issue-details/add-close-comment/add-close-comment.component';
import { ChangeIssueStatusRequest } from '../../../../core/projects.actions';
import { AnalyticsService } from '../../../../../../shared/services/analytics.service';
import { classifyFile } from '../../../../../../shared/helpers';
import { toolbarPickHasDesignContext, toolbarRecordLabel } from '../../../../../../shared/helpers/design-context';
import {
  isIssueStatusClosed,
  isIssueWorkflowEnabledForIssue,
  issuePageProjectId,
  IssueWorkflowProject,
  resolveIssueWorkflowProject,
  showLegacyIssueStatusControls,
} from '../../../../../../shared/helpers/issue-workflow';
import {
  StationRangeComponent,
} from '../../tab-screens/daily-report/daily-report-components/detail-components/control-forms-details/station-range/station-range.component';
import { Project } from '../../../../../../shared/models/project';
import { DrawingsAvailabilityService } from '../../../../../../shared/services/drawings-availability.service';
import { DrawingsAnalytics } from '../../../projects-details-page/components/tab-screens/drawings/drawings-analytics';

@Component({
    selector: 'app-actions-section',
    templateUrl: './actions-section.component.html',
    styleUrls: ['./actions-section.component.scss'],
    standalone: false
})
export class ActionsSectionComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInputRef: any;
  @Input() disableAddMenu = false;
  @Input() hideAddMenu = false;
  @Input() disableOptionMenu = false;
  @Input() hideDeleteOption = false;
  @Input() hideLocationOption = false;
  @Input() hideOptionMenu = false;
  @Input() disableDelete = false;
  @Input() label = 'Save';
  /** The host panel when the section alone is ambiguous: `bid_items` (multiline editor), `usage` (shares `delivery-usage`). */
  @Input() locationStationToolbarContext = '';
  status: any = 'open';
  isFormDirty = false;
  addMenuOptions = [
    {
      key: 'picture',
      icon: 'camera-outline',
      label: 'Picture',
      handler: () => {
        this.fileInputRef.nativeElement.click();
      },
    },
    {
      key: 'location',
      icon: 'marker-outline',
      label: 'Add location',
      handler: () => {
        this.openLocationPickerFromToolbar();
      },
    },
    {
      // W49: as on mobile, a separate action that opens the picker on its Drawing tab (projects with published sheets)
      key: 'mark_on_drawing',
      icon: 'markup-line',
      label: 'Mark on drawing',
      handler: () => {
        this.track.track('mark_on_drawing_clicked', { record_type: toolbarRecordLabel(this.route.snapshot.queryParams?.expandedSection, this.locationStationToolbarContext) });
        this.openLocationPickerFromToolbar('drawing');
      },
    },
    {
      key: 'station_range',
      icon: 'plus',
      label: 'Station range',
      handler: () => {
        this.openStationRangeDialog();
      },
    },
    {
      key: 'comment',
      icon: 'pencil-outline',
      label: 'Comment',
      handler: () => {
        this.dailyReportService.addComment.next(null);
      },
    },
  ];
  files: any[] = [];
  hideAddOption = false;
  issue: any;
  limits: any;
  addLabel = 'Add';
  dailyReport: any;
  editable: any;
  comments: string;
  dailyReportLocationConfig: any;
  appDataDetails: any;
  projectDetailsData: Project;
  /** W49: the project has published sheets, so "Mark on drawing" is offered */
  hasPublishedSheets = false;
  private sheetsCheckedFor: string | null = null;
  private readonly drawingsAvailability = inject(DrawingsAvailabilityService);
  private readonly track = inject(DrawingsAnalytics);
  /** The project last loaded; decides whether the open issue is on the contractor workflow. */
  private issueWorkflowProject: IssueWorkflowProject | null = null;
  private readonly router = inject(Router);
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private dailyReportService: DailyReportService,
    private store: Store<fromRoot.State>,
    public dialog: MatDialog,
    public route: ActivatedRoute,
    public projectsService: ProjectsService,
    public commonService: CommonService,
    public cdRef: ChangeDetectorRef,
    private ability: Ability,
    private analyticsService: AnalyticsService,
    private locationPickerLauncher: LocationPickerLauncherService,
  ) {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(params => {
        // tslint:disable-next-line:max-line-length
        if (
          params && params['expandedSection'] === 'activities' ||
          params.expandedSection === 'issues' ||
          params['expandedSection'] === undefined
        ) {
          this.hideAddOption = true;
          const index = this.addMenuOptions.findIndex(item => item.key === 'comment');
          if (index !== -1) {
            this.addMenuOptions.splice(index, 1);
          }
        }

        if (
          params.expandedSection === 'delivery-usage' ||
          params.expandedSection === 'labor-equipment' ||
          params.expandedSection === 'contractor-labor-equipment' ||
          params.expandedSection === 'weather' ||
          params.expandedSection === 'activity-codes' ||
          params.expandedSection === 'forms' ||
          params.expandedSection === 'testing-forms' ||
          params.expandedSection === 'notes'
        ) {
          this.hideAddOption = true;
        }

        if (
          params.expandedSection === 'issues' ||
          params['expandedSection'] === undefined
        ) {
          this.disableOptionMenu = true;
        }

        if (params.itType === 'withStations') {
          this.addLabel = 'Add Station';
        } else if (params.itType === 'withoutStations') {
          this.addLabel = 'Add Time';
        }
      });

    this.dailyReportService.setItType
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data === 'withStations') {
          this.addLabel = 'Add Station';
        } else if (data === 'withoutStations') {
          this.addLabel = 'Add Time';
        }
      });

    this.projectsService.isAnyFormDirty
      .pipe(takeUntil(this.onDestroy))
      .subscribe((dirty) => {
        this.isFormDirty = dirty;
      });

    this.dailyReportService.disableOptionMenu
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.disableOptionMenu = true;
      });

    this.dailyReportService.issue
      .pipe(takeUntil(this.onDestroy))
      .subscribe((issue) => {
        this.issue = issue;
        // Check permissions when issue is loaded
        if (issue) {
          // Derive site_id from entity for CASL condition matching
          const issueData = { ...issue };
          if (issueData.entity_type === 'site' && issueData.entity?.id) {
            issueData.site_id = issueData.entity.id;
          }
          const issueSubject = subject('Issue', issueData);
          this.editable = this.ability.can('update', issueSubject) || this.ability.can('create', issueSubject);
          this.applyWorkflowIssueEditability();
          this.cdRef.detectChanges();
        }
      });

    this.store.select(limits)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.limits = data;
      });

    this.store.select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.appDataDetails = data;
      });

    this.store.select(dailyReportLocationConfig)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.dailyReportLocationConfig = data;
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.projectDetailsData = data;
        this.checkPublishedSheets(data?.id);
        // Kept through a refetch (the store holds nothing while one is in flight).
        if (data?.id) {
          this.issueWorkflowProject = data;
          this.applyWorkflowIssueEditability();
        }
      });
  }

  ngOnInit() {
    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.dailyReport = { ...report };
        // For issues context, we'll set editable based on issue permissions when issue loads
        const queryParams = this.route.snapshot.queryParams;
        if (queryParams['expandedSection'] === 'issues' || !queryParams['expandedSection']) {
          // Only set editable if no issue is loaded yet;
          // once an issue is loaded, the issue subscription handles editable
          if (!this.issue) {
            this.editable = this.ability.can('create', 'Issue');
          }
          // Check if user can create new issues
          this.editable = this.ability.can('create', 'Issue');
          this.applyWorkflowIssueEditability();
        } else {
          // For non-issue contexts, check DailyReport permissions
          this.editable = this.ability.can('create', subject('DailyReport', this.dailyReport));
        }
      });

    this.dailyReportService.commentsValue
      .pipe(takeUntil(this.onDestroy))
      .subscribe(value => {
        this.comments = value;
      });
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;  // Get selected files
    const imageFormData = new FormData();
    const videoFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);

      if (classifyFile(file) === 'video') {
        videoFiles.push(file);
      } else {
        imageFormData.append('inputFiles', file);
      }
    }

    if (imageFormData.has('inputFiles')) {
      this.store.dispatch(UploadFileRequest({ payload: imageFormData }));

      const projectId = this.route.snapshot.paramMap.get('projectId');
      const siteId = this.route.snapshot.paramMap.get('siteId');
      this.analyticsService.capture('photo_uploaded', {
        project_id: projectId,
        site_id: siteId,
        count: files.length,
      });
    }

    if (videoFiles.length > 0) {
      videoFiles.forEach((videoFile: File) => {
        const videoElement = document.createElement('video');
        const reader = new FileReader();

        reader.onloadend = () => {
          videoElement.src = reader.result as string;

          videoElement.onloadedmetadata = () => {
            const videoDuration = videoElement.duration;
            const maxDuration = this.limits.upload.video_size_in_secs;

            if (videoDuration > maxDuration) {
              this.commonService.notification(`Video duration is ${videoDuration} seconds, should be less than ${maxDuration} seconds.`, 'danger');
            } else {
              this.store.dispatch(GetVideoIdRequest({ payload: videoFile }));
            }
          };
        };

        reader.readAsDataURL(videoFile);
      });
    }

    this.fileInputRef.nativeElement.value = '';
  }


  save() {
    this.dailyReportService.triggerSave.next(null);
  }

  addNew() {
    this.dailyReportService.add.next();
  }

  delete() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete',
        `Are you sure you want to delete this item?`,
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dailyReportService.triggerDelete.next(null);
      }
    });
  }

  /**
   * The issue open in the panel is on a project with the contractor workflow
   * (issue_workflow_enabled). Always false for a project without the flag.
   */
  get isWorkflowIssue(): boolean {
    // The daily report's issues do not say which project they are in: the page does.
    return !!this.issue?.id && isIssueWorkflowEnabledForIssue(
      this.issue,
      this.issueWorkflowProject,
      issuePageProjectId(this.router.url),
    );
  }

  /** The Close / Reopen entries of the menu: on a workflow project those are workflow actions instead. */
  get showLegacyIssueStatusControls(): boolean {
    return showLegacyIssueStatusControls(this.isWorkflowIssue);
  }

  /** Closed is the one status an issue is not open in: Close is for every other, Reopen for this one. */
  get isIssueClosed(): boolean {
    return isIssueStatusClosed(this.issue?.status);
  }

  /** With Close / Reopen gone, the menu of an existing workflow issue has nothing left in it. */
  get isWorkflowIssueMenuEmpty(): boolean {
    return this.isWorkflowIssue && this.hideAddOption && this.hideDeleteOption;
  }

  /**
   * On a workflow project the Save bar follows the real `update` ability on
   * the open issue: `create` there does not imply editing (a contractor PM
   * edits only the issues they raised). Does nothing off the workflow, where
   * the checks above stand as they were.
   */
  private applyWorkflowIssueEditability(): void {
    if (!this.isWorkflowIssue) {
      return;
    }
    const issueData = { ...this.issue };
    if (issueData.entity_type === 'site' && issueData.entity?.id) {
      issueData.site_id = issueData.entity.id;
    }
    if (!issueData.project_id) {
      issueData.project_id = resolveIssueWorkflowProject(
        this.issue,
        this.issueWorkflowProject,
        issuePageProjectId(this.router.url),
      )?.id;
    }
    this.editable = this.ability.can('update', subject('Issue', issueData));
  }

  setStatus(data) {
    // Closing and reopening are workflow actions on a contractor-workflow project.
    if (!this.showLegacyIssueStatusControls) {
      return;
    }
    const { status, ...rest } = this.issue;
    const payload = {
      ...rest,
      status: data,
      qp: {
        include: [
          'createdBy',
          'bid_item',
          'bid_items',
          'entity',
          'pictures',
          'attachments',
          'material_tests',
          'map_feature_link',
        ],
      },
    };

    if (data === 'close') {
      this.dailyReportService.fetchComments.next();
      this.addCloseCommentDialog({ payload, comments: this.comments });
    } else {
      this.store.dispatch(ChangeIssueStatusRequest({ payload }));
    }
  }

  isAddMenuOptionHidden(option) {
    if (this.hideLocationOption && (option.key === 'location' || option.key === 'station_range' || option.key === 'mark_on_drawing')) {
      return true;
    }

    // W49: only with published sheets, and only where the record can hold a drawing link
    if (option.key === 'mark_on_drawing') {
      return !this.hasPublishedSheets || !this.pickHasDesignContext();
    }

    if (option.key === 'station_range') {
      return !this.isStationRangeLocationEnabled();
    }

    return false;
  }

  isStationRangeLocationEnabled() {
    return this.getResolvedDailyReportLocationConfig()?.mode === 'station_range' &&
      this.isStationRangeEnabledForCurrentSection();
  }

  isStationRangeEnabledForCurrentSection() {
    const widgets = this.getResolvedDailyReportLocationConfig()?.widgets || [];
    if (!widgets.length) {
      return true;
    }

    const expandedSection = this.route.snapshot.queryParams?.expandedSection;
    const sectionWidgets = {
      'item-postings': ['bid_items'],
      'delivery-usage': ['deliveries'],
      'material-testing': ['testing'],
      issues: ['issues'],
      activities: ['activities'],
      'labor-equipment': ['labor', 'equipment'],
      'contractor-labor-equipment': ['labor', 'equipment'],
      'activity-codes': ['activity_codes'],
      forms: ['control_forms'],
      'testing-forms': ['control_forms'],
    };
    const matchingWidgets = sectionWidgets[expandedSection] || ['issues'];

    return matchingWidgets.some(widget => widgets.includes(widget));
  }

  getResolvedDailyReportLocationConfig() {
    const projectMode = this.projectDetailsData?.daily_report_location_mode;
    const ownerOrganizationMode = this.projectDetailsData?.owner_organization?.daily_report_location_mode;
    const appDataMode = this.dailyReportLocationConfig?.mode;
    const mode = [projectMode, ownerOrganizationMode, appDataMode]
      .find(value => ['pin', 'station_range'].includes(value));

    return {
      ...(this.dailyReportLocationConfig || {}),
      ...(mode ? { mode } : {}),
    };
  }

  /** W49: whether the project has published sheets (the picker's own Drawing tab check), once per project */
  private checkPublishedSheets(projectId: string | null | undefined): void {
    if (!projectId || projectId === this.sheetsCheckedFor) {
      return;
    }
    this.sheetsCheckedFor = projectId;
    this.hasPublishedSheets = false;
    this.drawingsAvailability.available(projectId).then(found => {
      if (this.sheetsCheckedFor === projectId) {
        this.hasPublishedSheets = found;
        this.cdRef.markForCheck();
      }
    }).catch(() => undefined);
  }

  openLocationPickerFromToolbar(initialTab?: 'drawing') {
    const expandedSection = this.route.snapshot.queryParams?.expandedSection;
    const isItemPostingsSection = expandedSection === 'item-postings'
      || this.locationStationToolbarContext === 'bid_items';
    this.store.select(currentLocation)
      .subscribe(location => {
        const fallbackLocation = this.getFallbackLocation(location);

        // An open form editor shares its feature_source and link (formPickerContext); otherwise the toolbar doesn't
        // know the record's link: no existingLink, so the pick leaves it untouched unless the user links.
        const formContext = ['forms', 'control-forms'].includes(expandedSection) ? this.dailyReportService.formPickerContext?.() : null;
        const formPin = formContext?.latitude && formContext?.longitude;
        this.locationPickerLauncher.open({
          latitude: formPin ? formContext.latitude : (formContext?.existingLink ? null : fallbackLocation?.latitude),
          longitude: formPin ? formContext.longitude : (formContext?.existingLink ? null : fallbackLocation?.longitude),
          ...(formContext ? { existingLink: formContext.existingLink, featureSource: formContext.featureSource } : {}),
          location: fallbackLocation?.location,
          enableLocationSection: this.isStationRangeLocationEnabled(),
          showIncludeGpsLocationCheckbox:
            !!this.appDataDetails?.enableAashtoIntegration && isItemPostingsSection,
          locationText: fallbackLocation?.location,
          recordLabel: toolbarRecordLabel(expandedSection, this.locationStationToolbarContext),
          designContext: this.pickHasDesignContext(),
          ...(initialTab ? { initialTab } : {}),
        }).catch(error => console.error('Location picker failed to open', error));
      }).unsubscribe();
  }

  /** Item postings and usage have no map_feature_link control: see `toolbarPickHasDesignContext`. */
  private pickHasDesignContext(): boolean {
    return toolbarPickHasDesignContext(this.route.snapshot.queryParams?.expandedSection, this.locationStationToolbarContext);
  }

  getFallbackLocation(location) {
    if (location?.latitude && location?.longitude) {
      return location;
    }

    let fallbackLocation = location || {};
    this.store.select(siteDetails)
      .subscribe(site => {
        if (site?.longitude && site?.latitude) {
          fallbackLocation = {
            ...fallbackLocation,
            latitude: site.latitude,
            longitude: site.longitude,
          };
        } else {
          this.store.select(projectDetails)
            .subscribe(project => {
              if (project?.longitude && project?.latitude) {
                fallbackLocation = {
                  ...fallbackLocation,
                  latitude: project.latitude,
                  longitude: project.longitude,
                };
              }
            }).unsubscribe();
        }
      }).unsubscribe();

    return fallbackLocation;
  }

  openStationRangeDialog() {
    const values = this.locationStationToolbarContext === 'bid_items'
      ? this.dailyReportService.locationStationToolbarState || {}
      : {};
    this.dialog.open(StationRangeComponent, {
      disableClose: true,
      width: '1200px',
      height: '620px',
      maxWidth: 'calc(100vw - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      panelClass: 'station-range-dialog-panel',
      data: {
        emitSelectedLocation: true,
        form: new FormGroup({
          alignment: new FormControl(values.alignment || null),
          stationRangeFromStation: new FormControl(values.stationRangeFromStation || null),
          stationRangeFromOffsetType: new FormControl(values.stationRangeFromOffsetType || null),
          stationRangeFromOffsetDistance: new FormControl(values.stationRangeFromOffsetDistance || null),
          stationRangeToStation: new FormControl(values.stationRangeToStation || null),
          stationRangeToOffsetType: new FormControl(values.stationRangeToOffsetType || null),
          stationRangeToOffsetDistance: new FormControl(values.stationRangeToOffsetDistance || null),
        }),
      },
    });
  }

  handleLocation() {
    let location = {};
    navigator.geolocation.getCurrentPosition(
      (position) => {
        location = {
          longitude: position?.coords?.longitude || null,
          latitude: position?.coords?.latitude || null,
        };
        this.openLocationPicker(location);
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          this.store.select(siteDetails)
            .subscribe(site => {
              if (site?.longitude && site?.latitude) {
                location = {
                  latitude: site?.latitude,
                  longitude: site?.longitude,
                };
                this.openLocationPicker(location);
              } else {
                this.store.select(projectDetails)
                  .subscribe(project => {
                    if (project?.longitude && project?.latitude) {
                      location = {
                        latitude: project?.latitude,
                        longitude: project?.longitude,
                      };
                      this.openLocationPicker(location);
                    }
                  }).unsubscribe();
              }
            }).unsubscribe();
        }
      });

  }

  openLocationPicker(location) {
    this.locationPickerLauncher.open({
      latitude: location?.latitude,
      longitude: location?.longitude,
      designContext: this.pickHasDesignContext(),
    }).catch(error => console.error('Location picker failed to open', error));
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  addCloseCommentDialog(data) {
    this.dialog.open(AddCloseCommentComponent, {
      disableClose: true,
      width: '600px',
      height: '500px',
      data: {
        changeIssueStatusRequestPayload: data.payload,
        comments: data.comments,
      },
    });
  }
}
